import React, { useEffect, useRef } from 'react';
import useGameStore from '../engine/store';

const MSG_STYLES = {
  player: {
    align: 'justify-end',
    bg: 'bg-neon-blue/15 border-neon-blue/20',
    text: 'text-white',
  },
  opponent: {
    align: 'justify-start',
    bg: 'bg-dark-600/60 border-white/5',
    text: 'text-gray-200',
  },
  system: {
    align: 'justify-center',
    bg: 'bg-transparent border-transparent',
    text: 'text-gray-500',
  },
};

const TYPE_BADGES = {
  offer: { label: 'OFFER', color: '#00aaff' },
  bluff: { label: 'BLUFF', color: '#ff8800' },
  pressure: { label: 'PRESSURE', color: '#ff3366' },
  hold: { label: 'HOLD', color: '#ffcc00' },
  accept: { label: 'DEAL', color: '#00ff88' },
  walkaway: { label: 'WALK', color: '#ff3366' },
  scenario: { label: 'SCENARIO', color: '#aa55ff' },
  info: { label: 'INFO', color: '#00aaff' },
  objective: { label: 'GOAL', color: '#ffcc00' },
  end: { label: 'END', color: '#ff8800' },
};

export default function ChatPanel() {
  const { chatLog, opponentTyping } = useGameStore();
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatLog, opponentTyping]);

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-3">
      {chatLog.map((msg, i) => {
        const style = MSG_STYLES[msg.sender] || MSG_STYLES.system;
        const badge = msg.type ? TYPE_BADGES[msg.type] : null;

        if (msg.sender === 'system') {
          return (
            <div key={i} className="flex justify-center animate-slide-up">
              <div className="max-w-md text-center">
                {badge && (
                  <span
                    className="text-xs font-black uppercase tracking-widest mr-2"
                    style={{ color: badge.color }}
                  >
                    {badge.label}
                  </span>
                )}
                <span className="text-xs text-gray-500">{msg.text}</span>
              </div>
            </div>
          );
        }

        return (
          <div key={i} className={`flex ${style.align} animate-slide-up`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-3 border ${style.bg} ${
              msg.sender === 'player' ? 'rounded-br-md' : 'rounded-bl-md'
            }`}>
              {/* Badge */}
              {badge && (
                <div className="mb-1">
                  <span
                    className="text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded"
                    style={{ background: `${badge.color}20`, color: badge.color }}
                  >
                    {badge.label}
                  </span>
                </div>
              )}

              {/* Sender label */}
              <div className="text-[10px] font-semibold mb-0.5" style={{
                color: msg.sender === 'player' ? '#00aaff' : msg.sender === 'opponent' ? '#ff8800' : '#666'
              }}>
                {msg.sender === 'player' ? 'You' : msg.sender === 'opponent' ? 'Opponent' : ''}
              </div>

              {/* Message */}
              <div className={`text-sm leading-relaxed ${style.text}`}>
                {msg.text}
              </div>
            </div>
          </div>
        );
      })}

      {/* Typing indicator */}
      {opponentTyping && (
        <div className="flex justify-start animate-slide-up">
          <div className="rounded-2xl rounded-bl-md px-4 py-3 bg-dark-600/60 border border-white/5">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-gray-500 animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-2 h-2 rounded-full bg-gray-500 animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-2 h-2 rounded-full bg-gray-500 animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  );
}
